//File: src/pages/Event/EventReportsDashboard.jsx

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api from "@/lib/axios";
import toast from "react-hot-toast";
import {
  CalendarRange,
  Search,
  FileText,
  FileSpreadsheet,
  Loader2,
  Filter,
  Sparkles,
  BarChart3,
  FolderOpen,
  Download,
  RefreshCw,
  CheckCircle2,
  AlertTriangle,
  ArrowUpRight,
  ExternalLink,
  Eye,
  Code2,
} from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";

function safeNum(v, d = 0) {
  const n = Number(v);
  return Number.isFinite(n) ? n : d;
}

function money(v) {
  return `£${safeNum(v, 0).toFixed(2)}`;
}

function pct(v) {
  return `${safeNum(v, 0).toFixed(2)}%`;
}

function normalizeDateOnlyString(v) {
  if (!v) return "";
  const raw = String(v).trim();

  if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) {
    return raw;
  }

  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return "";

  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
}

function toDateInput(v) {
  return normalizeDateOnlyString(v);
}

function formatDate(v) {
  if (!v) return "—";
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return String(v);
  return d.toLocaleDateString("en-GB", {
    year: "numeric",
    month: "short",
    day: "2-digit",
  });
}

function startOfMonthInput() {
  const d = new Date();
  d.setDate(1);
  return normalizeDateOnlyString(d);
}

function todayInput() {
  return normalizeDateOnlyString(new Date());
}

const LIST_ENDPOINT_CANDIDATES = [
  "/solo-mdsh-api/event/events",
  "/solo-mdsh-api/event",
  "/solo-mdsh-api/events",
];

function fileSafeName(v, fallback = "event-report") {
  const cleaned = String(v || fallback)
    .replace(/[^\w\s-]/g, "")
    .trim()
    .replace(/\s+/g, "-");
  return cleaned || fallback;
}

async function fetchEventsFromFirstWorkingEndpoint(params = {}) {
  let lastError = null;

  for (const endpoint of LIST_ENDPOINT_CANDIDATES) {
    try {
      const { data } = await api.get(endpoint, { params });

      if (Array.isArray(data)) return data;
      if (Array.isArray(data?.events)) return data.events;
      if (Array.isArray(data?.rows)) return data.rows;
      if (Array.isArray(data?.data)) return data.data;
    } catch (err) {
      lastError = err;
    }
  }

  throw lastError || new Error("No compatible event list endpoint found");
}

async function apiGetFirst(paths, config = {}) {
  let lastError = null;

  for (const path of paths) {
    try {
      const response = await api.get(path, config);
      return { response, usedPath: path };
    } catch (err) {
      lastError = err;
    }
  }

  throw lastError || new Error("No compatible endpoint found");
}

async function downloadBlobFromFirst(urls, filename, mimeType) {
  const { response, usedPath } = await apiGetFirst(urls, { responseType: "blob" });
  const blob = new Blob([response.data], { type: mimeType });
  const objectUrl = window.URL.createObjectURL(blob);

  const a = document.createElement("a");
  a.href = objectUrl;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();

  window.URL.revokeObjectURL(objectUrl);

  return usedPath;
}

const REPORT_PACK_ENDPOINTS = (eventId) => [
  `/solo-mdsh-api/event/${eventId}/report-pack`,
  `/solo-mdsh-api/events/${eventId}/report-pack`,
];

const REPORT_PDF_ENDPOINTS = (eventId) => [
  `/solo-mdsh-api/event/${eventId}/report/pdf`,
  `/solo-mdsh-api/event/${eventId}/pdf`,
  `/solo-mdsh-api/events/${eventId}/report/pdf`,
  `/solo-mdsh-api/events/${eventId}/pdf`,
];

const REPORT_XLSX_ENDPOINTS = (eventId) => [
  `/solo-mdsh-api/event/${eventId}/report/xlsx`,
  `/solo-mdsh-api/event/${eventId}/report/excel`,
  `/solo-mdsh-api/event/${eventId}/xlsx`,
  `/solo-mdsh-api/event/${eventId}/excel`,
  `/solo-mdsh-api/events/${eventId}/report/xlsx`,
  `/solo-mdsh-api/events/${eventId}/report/excel`,
  `/solo-mdsh-api/events/${eventId}/xlsx`,
  `/solo-mdsh-api/events/${eventId}/excel`,
];

function EventMetricCard({ label, value, valueClassName = "text-white" }) {
  return (
    <div className="rounded-xl border border-slate-800 bg-slate-900/80 p-4">
      <div className="text-xs uppercase tracking-wide text-slate-500">{label}</div>
      <div className={`mt-1 text-2xl font-semibold ${valueClassName}`}>{value}</div>
    </div>
  );
}

export default function EventReportsDashboard() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const seededEventId = searchParams.get("eventId");

  const [events, setEvents] = useState([]);
  const [selectedIds, setSelectedIds] = useState(seededEventId ? [String(seededEventId)] : []);
  const [search, setSearch] = useState("");
  const [startDate, setStartDate] = useState(startOfMonthInput());
  const [endDate, setEndDate] = useState(todayInput());
  const [statusFilter, setStatusFilter] = useState("all");

  const [loadingEvents, setLoadingEvents] = useState(false);
  const [loadingSummary, setLoadingSummary] = useState(false);
  const [selectedSummaries, setSelectedSummaries] = useState([]);
  const [busyKey, setBusyKey] = useState("");

  const fetchEvents = useCallback(async () => {
    try {
      setLoadingEvents(true);

      const raw = await fetchEventsFromFirstWorkingEndpoint();

      const normalized = (raw || []).map((row, index) => {
        const eventId =
          row?.event_id ??
          row?.id ??
          row?.eventId ??
          row?.event?.event_id ??
          `row-${index}`;

        const eventName =
          row?.event_name ??
          row?.name ??
          row?.title ??
          row?.event?.event_name ??
          `Event ${eventId}`;

        const eventDate =
          row?.event_date ??
          row?.date ??
          row?.start_date ??
          row?.created_at ??
          row?.event?.event_date ??
          null;

        const venueName =
          row?.venue_name ??
          row?.venue ??
          row?.location ??
          row?.event?.venue_name ??
          "—";

        const plannerName =
          row?.planner_name ??
          row?.planner ??
          row?.event?.planner_name ??
          "—";

        const status = row?.status ?? row?.event_status ?? row?.quote_status ?? "Unknown";

        return {
          ...row,
          event_id: String(eventId),
          event_name: eventName,
          event_date: eventDate,
          venue_name: venueName,
          planner_name: plannerName,
          status,
        };
      });

      setEvents(normalized);
    } catch (err) {
      console.error("Failed to fetch events:", err);
      toast.error(err?.response?.data?.error || "Failed to load events");
    } finally {
      setLoadingEvents(false);
    }
  }, []);

  useEffect(() => {
    fetchEvents();
  }, [fetchEvents]);

  useEffect(() => {
    if (!seededEventId || !events.length) return;

    const exists = events.some((e) => String(e.event_id) === String(seededEventId));
    if (!exists) {
      setSelectedIds((prev) => prev.filter((id) => id !== String(seededEventId)));
    }
  }, [seededEventId, events]);

  const filteredEvents = useMemo(() => {
    const q = search.trim().toLowerCase();

    return events.filter((event) => {
      const eventDate = toDateInput(event.event_date);
      const inStart = !startDate || !eventDate || eventDate >= startDate;
      const inEnd = !endDate || !eventDate || eventDate <= endDate;

      const matchesStatus =
        statusFilter === "all" || String(event.status || "").toLowerCase() === statusFilter.toLowerCase();

      const haystack = [
        event.event_name,
        event.event_id,
        event.venue_name,
        event.planner_name,
        event.status,
      ]
        .join(" ")
        .toLowerCase();

      const matchesSearch = !q || haystack.includes(q);

      return inStart && inEnd && matchesStatus && matchesSearch;
    });
  }, [events, search, startDate, endDate, statusFilter]);

  const selectedEvents = useMemo(() => {
    const selectedSet = new Set(selectedIds);
    return events.filter((event) => selectedSet.has(String(event.event_id)));
  }, [events, selectedIds]);

  const visibleSelectedCount = useMemo(() => {
    const visibleSet = new Set(filteredEvents.map((e) => String(e.event_id)));
    return selectedIds.filter((id) => visibleSet.has(String(id))).length;
  }, [filteredEvents, selectedIds]);

  const toggleSelected = (eventId) => {
    setSelectedIds((prev) => {
      const key = String(eventId);
      return prev.includes(key) ? prev.filter((x) => x !== key) : [...prev, key];
    });
  };

  const selectAllFiltered = () => {
    setSelectedIds(filteredEvents.map((e) => String(e.event_id)));
  };

  const addAllFilteredToSelection = () => {
    setSelectedIds((prev) => {
      const merged = new Set([...prev, ...filteredEvents.map((e) => String(e.event_id))]);
      return Array.from(merged);
    });
  };

  const clearSelection = () => {
    setSelectedIds([]);
    setSelectedSummaries([]);
  };

  const loadSelectedSummaries = async () => {
    if (!selectedIds.length) {
      toast.error("Select at least one event first");
      return;
    }

    try {
      setLoadingSummary(true);

      const results = await Promise.all(
        selectedIds.map(async (eventId) => {
          try {
            const { response, usedPath } = await apiGetFirst(REPORT_PACK_ENDPOINTS(eventId));
            return {
              ok: true,
              event_id: String(eventId),
              data: response.data,
              source: usedPath,
            };
          } catch (err) {
            return {
              ok: false,
              event_id: String(eventId),
              error: err?.response?.data?.error || "Failed to load report pack",
            };
          }
        })
      );

      setSelectedSummaries(results);

      const okCount = results.filter((x) => x.ok).length;
      const failedCount = results.length - okCount;

      if (okCount > 0 && failedCount === 0) {
        toast.success("Selected event summaries loaded");
      } else if (okCount > 0 && failedCount > 0) {
        toast.success(`${okCount} summaries loaded, ${failedCount} failed`);
      } else {
        toast.error("No selected event summaries could be loaded");
      }
    } catch (err) {
      console.error("Failed to load selected report summaries:", err);
      toast.error("Failed to load selected summaries");
    } finally {
      setLoadingSummary(false);
    }
  };

  const aggregate = useMemo(() => {
    const okItems = selectedSummaries.filter((x) => x.ok && x.data);
    const failedItems = selectedSummaries.filter((x) => !x.ok);

    const summary = okItems.reduce(
      (acc, item) => {
        const pack = item.data || {};
        const ms = pack.management_summary || {};
        const readiness = safeNum(pack?.analysis?.readiness?.score, 0);

        acc.loaded_count += 1;
        acc.quote_total += safeNum(ms.quote_total);
        acc.actual_total_revenue += safeNum(ms.actual_total_revenue);
        acc.balance_due += safeNum(ms.balance_due);
        acc.gross_margin_sum += safeNum(ms.gross_margin_pct);
        acc.net_margin_sum += safeNum(ms.net_margin_pct);
        acc.readiness_sum += readiness;

        return acc;
      },
      {
        selected_count: selectedIds.length,
        loaded_count: 0,
        failed_count: failedItems.length,
        quote_total: 0,
        actual_total_revenue: 0,
        balance_due: 0,
        gross_margin_sum: 0,
        net_margin_sum: 0,
        readiness_sum: 0,
      }
    );

    return {
      ...summary,
      avg_gross_margin_pct: summary.loaded_count ? summary.gross_margin_sum / summary.loaded_count : 0,
      avg_net_margin_pct: summary.loaded_count ? summary.net_margin_sum / summary.loaded_count : 0,
      avg_readiness: summary.loaded_count ? summary.readiness_sum / summary.loaded_count : 0,
    };
  }, [selectedSummaries, selectedIds.length]);

  const handleSingleDownload = async (event, type) => {
    const key = `${type}-${event.event_id}`;
    try {
      setBusyKey(key);

      if (type === "pdf") {
        const usedPath = await downloadBlobFromFirst(
          REPORT_PDF_ENDPOINTS(event.event_id),
          `${fileSafeName(event.event_name, `event-${event.event_id}`)}-report.pdf`,
          "application/pdf"
        );
        toast.success(`PDF downloaded (${usedPath})`);
      } else {
        const usedPath = await downloadBlobFromFirst(
          REPORT_XLSX_ENDPOINTS(event.event_id),
          `${fileSafeName(event.event_name, `event-${event.event_id}`)}-report.xlsx`,
          "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
        );
        toast.success(`Excel downloaded (${usedPath})`);
      }
    } catch (err) {
      console.error(`Failed to download ${type} for event ${event.event_id}:`, err);
      toast.error(err?.response?.data?.error || `Failed to download ${type.toUpperCase()}`);
    } finally {
      setBusyKey("");
    }
  };

  const handleOpenDebugJson = async (eventId) => {
    try {
      const { response, usedPath } = await apiGetFirst(REPORT_PACK_ENDPOINTS(eventId));
      const blob = new Blob([JSON.stringify(response.data, null, 2)], {
        type: "application/json",
      });
      const objectUrl = window.URL.createObjectURL(blob);

      window.open(objectUrl, "_blank", "noopener,noreferrer");

      setTimeout(() => {
        window.URL.revokeObjectURL(objectUrl);
      }, 5000);

      toast.success(`Debug JSON opened (${usedPath})`);
    } catch (err) {
      console.error(`Failed to open debug JSON for event ${eventId}:`, err);
      toast.error(err?.response?.data?.error || "Failed to open debug JSON");
    }
  };

  const handleViewReport = (eventId) => {
    navigate(`/events/${eventId}/report-view`);
  };

  const statuses = useMemo(() => {
    const set = new Set(
      events
        .map((x) => String(x.status || "").trim())
        .filter(Boolean)
    );
    return ["all", ...Array.from(set)];
  }, [events]);

  return (
    <div className="min-h-screen bg-slate-950 px-4 py-6 text-slate-100 md:px-6 xl:px-8">
      <div className="mx-auto flex max-w-7xl flex-col gap-6">
        <div className="rounded-2xl border border-slate-800 bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 p-6 shadow-2xl">
          <div className="flex flex-col gap-4 xl:flex-row xl:items-center xl:justify-between">
            <div>
              <div className="mb-2 flex items-center gap-2">
                <Badge className="border-cyan-500/30 bg-cyan-500/10 text-cyan-300 hover:bg-cyan-500/10">
                  Event Reporting
                </Badge>
                <Badge className="border-fuchsia-500/30 bg-fuchsia-500/10 text-fuchsia-300 hover:bg-fuchsia-500/10">
                  Single + Period Mode
                </Badge>
              </div>

              <h1 className="flex items-center gap-3 text-2xl font-bold text-white md:text-3xl">
                <BarChart3 className="h-8 w-8 text-cyan-400" />
                Event Reports Dashboard
              </h1>

              <p className="mt-2 max-w-3xl text-sm text-slate-400 md:text-base">
                Review one event or many events over a selected time period, inspect report intelligence,
                launch the live viewer, and export management-grade PDF and Excel packs.
              </p>
            </div>

            <div className="flex flex-wrap gap-3">
              <Button
                onClick={fetchEvents}
                disabled={loadingEvents}
                variant="outline"
                className="border-slate-700 bg-slate-900 text-slate-100 hover:bg-slate-800"
              >
                {loadingEvents ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <RefreshCw className="mr-2 h-4 w-4" />
                )}
                Refresh
              </Button>

              <Button
                onClick={loadSelectedSummaries}
                disabled={loadingSummary || !selectedIds.length}
                className="bg-cyan-600 text-white hover:bg-cyan-500"
              >
                {loadingSummary ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <Sparkles className="mr-2 h-4 w-4" />
                )}
                Analyze Selected
              </Button>
            </div>
          </div>
        </div>

        <Card className="border-slate-800 bg-slate-950/70 text-slate-100">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-white">
              <Filter className="h-5 w-5 text-cyan-400" />
              Filters
            </CardTitle>
          </CardHeader>

          <CardContent>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-5">
              <div className="xl:col-span-2">
                <label className="mb-2 block text-sm text-slate-400">Search</label>
                <div className="relative">
                  <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500" />
                  <Input
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Search by event, ID, venue, planner, status..."
                    className="border-slate-800 bg-slate-900 pl-9 text-slate-100 placeholder:text-slate-500"
                  />
                </div>
              </div>

              <div>
                <label className="mb-2 block text-sm text-slate-400">Start Date</label>
                <Input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="border-slate-800 bg-slate-900 text-slate-100"
                />
              </div>

              <div>
                <label className="mb-2 block text-sm text-slate-400">End Date</label>
                <Input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  className="border-slate-800 bg-slate-900 text-slate-100"
                />
              </div>

              <div>
                <label className="mb-2 block text-sm text-slate-400">Status</label>
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="h-10 w-full rounded-md border border-slate-800 bg-slate-900 px-3 text-sm text-slate-100 outline-none"
                >
                  {statuses.map((status) => (
                    <option key={status} value={status}>
                      {status === "all" ? "All statuses" : status}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="mt-4 flex flex-wrap gap-3">
              <Button
                variant="outline"
                onClick={selectAllFiltered}
                className="border-slate-700 bg-slate-900 text-slate-100 hover:bg-slate-800"
              >
                Select All Filtered
              </Button>

              <Button
                variant="outline"
                onClick={addAllFilteredToSelection}
                className="border-slate-700 bg-slate-900 text-slate-100 hover:bg-slate-800"
              >
                Add Filtered to Selection
              </Button>

              <Button
                variant="outline"
                onClick={clearSelection}
                className="border-slate-700 bg-slate-900 text-slate-100 hover:bg-slate-800"
              >
                Clear Selection
              </Button>

              <Badge className="border-emerald-500/30 bg-emerald-500/10 text-emerald-300 hover:bg-emerald-500/10">
                {filteredEvents.length} events in period
              </Badge>

              <Badge className="border-cyan-500/30 bg-cyan-500/10 text-cyan-300 hover:bg-cyan-500/10">
                {selectedIds.length} selected
              </Badge>

              <Badge className="border-violet-500/30 bg-violet-500/10 text-violet-300 hover:bg-violet-500/10">
                {visibleSelectedCount} selected in current filtered view
              </Badge>
            </div>
          </CardContent>
        </Card>

        {selectedSummaries.length > 0 && (
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-5">
            <Card className="border-slate-800 bg-slate-950/70 text-slate-100 lg:col-span-5">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-white">
                  <CalendarRange className="h-5 w-5 text-fuchsia-400" />
                  Selected Period Summary
                </CardTitle>
              </CardHeader>

              <CardContent>
                <div className="mb-4 grid grid-cols-1 gap-3 sm:grid-cols-3 xl:grid-cols-6">
                  <EventMetricCard label="Selected" value={aggregate.selected_count} />
                  <EventMetricCard label="Loaded" value={aggregate.loaded_count} valueClassName="text-emerald-300" />
                  <EventMetricCard label="Failed" value={aggregate.failed_count} valueClassName="text-rose-300" />
                  <EventMetricCard label="Quote Total" value={money(aggregate.quote_total)} valueClassName="text-emerald-300" />
                  <EventMetricCard
                    label="Actual Revenue"
                    value={money(aggregate.actual_total_revenue)}
                    valueClassName="text-cyan-300"
                  />
                  <EventMetricCard
                    label="Outstanding Balance"
                    value={money(aggregate.balance_due)}
                    valueClassName="text-rose-300"
                  />
                </div>

                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3">
                  <EventMetricCard
                    label="Avg Gross Margin"
                    value={pct(aggregate.avg_gross_margin_pct)}
                    valueClassName="text-violet-300"
                  />
                  <EventMetricCard
                    label="Avg Net Margin"
                    value={pct(aggregate.avg_net_margin_pct)}
                    valueClassName="text-fuchsia-300"
                  />
                  <EventMetricCard label="Avg Readiness" value={pct(aggregate.avg_readiness)} />
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        <Card className="border-slate-800 bg-slate-950/70 text-slate-100">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-white">
              <FolderOpen className="h-5 w-5 text-cyan-400" />
              Events
            </CardTitle>
          </CardHeader>

          <CardContent>
            {loadingEvents ? (
              <div className="flex items-center justify-center py-14 text-slate-400">
                <Loader2 className="mr-3 h-5 w-5 animate-spin" />
                Loading events...
              </div>
            ) : filteredEvents.length === 0 ? (
              <div className="rounded-xl border border-dashed border-slate-800 bg-slate-900/40 p-8 text-center text-slate-400">
                No events found for the current filters.
              </div>
            ) : (
              <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
                {filteredEvents.map((event) => {
                  const isSelected = selectedIds.includes(String(event.event_id));
                  const summary = selectedSummaries.find((x) => String(x.event_id) === String(event.event_id));
                  const summaryData = summary?.ok ? summary.data : null;

                  return (
                    <div
                      key={event.event_id}
                      className={`rounded-2xl border p-5 transition-all ${
                        isSelected
                          ? "border-cyan-500/40 bg-cyan-500/5 shadow-[0_0_0_1px_rgba(34,211,238,0.15)]"
                          : "border-slate-800 bg-slate-900/70"
                      }`}
                    >
                      <div className="flex flex-col gap-4">
                        <div className="flex items-start justify-between gap-3">
                          <div>
                            <h3 className="text-lg font-semibold text-white">{event.event_name}</h3>
                            <p className="mt-1 text-sm text-slate-400">
                              Event #{event.event_id} • {formatDate(event.event_date)}
                            </p>
                          </div>

                          <button
                            type="button"
                            onClick={() => toggleSelected(event.event_id)}
                            className={`rounded-full border px-3 py-1 text-xs font-medium transition ${
                              isSelected
                                ? "border-cyan-400 bg-cyan-500/10 text-cyan-300"
                                : "border-slate-700 bg-slate-900 text-slate-300"
                            }`}
                          >
                            {isSelected ? "Selected" : "Select"}
                          </button>
                        </div>

                        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                          <div className="rounded-xl border border-slate-800 bg-slate-950/70 p-3">
                            <div className="text-xs uppercase tracking-wide text-slate-500">Venue</div>
                            <div className="mt-1 text-sm text-slate-200">{event.venue_name || "—"}</div>
                          </div>

                          <div className="rounded-xl border border-slate-800 bg-slate-950/70 p-3">
                            <div className="text-xs uppercase tracking-wide text-slate-500">Planner</div>
                            <div className="mt-1 text-sm text-slate-200">{event.planner_name || "—"}</div>
                          </div>

                          <div className="rounded-xl border border-slate-800 bg-slate-950/70 p-3">
                            <div className="text-xs uppercase tracking-wide text-slate-500">Status</div>
                            <div className="mt-1 text-sm text-slate-200">{event.status || "—"}</div>
                          </div>
                        </div>

                        {summaryData ? (
                          <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
                            <div className="rounded-xl border border-slate-800 bg-slate-950/70 p-3">
                              <div className="text-xs uppercase tracking-wide text-slate-500">Quote</div>
                              <div className="mt-1 font-semibold text-emerald-300">
                                {money(summaryData?.management_summary?.quote_total)}
                              </div>
                            </div>

                            <div className="rounded-xl border border-slate-800 bg-slate-950/70 p-3">
                              <div className="text-xs uppercase tracking-wide text-slate-500">Actual</div>
                              <div className="mt-1 font-semibold text-cyan-300">
                                {summaryData?.management_summary?.actual_total_revenue == null
                                  ? "—"
                                  : money(summaryData?.management_summary?.actual_total_revenue)}
                              </div>
                            </div>

                            <div className="rounded-xl border border-slate-800 bg-slate-950/70 p-3">
                              <div className="text-xs uppercase tracking-wide text-slate-500">Gross Margin</div>
                              <div className="mt-1 font-semibold text-violet-300">
                                {pct(summaryData?.management_summary?.gross_margin_pct)}
                              </div>
                            </div>

                            <div className="rounded-xl border border-slate-800 bg-slate-950/70 p-3">
                              <div className="text-xs uppercase tracking-wide text-slate-500">Readiness</div>
                              <div className="mt-1 font-semibold capitalize text-white">
                                {summaryData?.analysis?.readiness?.status || "—"}
                              </div>
                            </div>
                          </div>
                        ) : summary?.ok === false ? (
                          <div className="rounded-xl border border-rose-500/20 bg-rose-500/5 p-3 text-sm text-rose-300">
                            {summary.error || "This event report could not be loaded."}
                          </div>
                        ) : null}

                        <div className="rounded-xl border border-slate-800 bg-slate-950/40 p-3">
                          <div className="mb-3 text-xs font-semibold uppercase tracking-wide text-slate-500">
                            Actions
                          </div>

                          <div className="flex flex-wrap gap-3">
                            <Button
                              size="sm"
                              onClick={() => navigate(`/events/${event.event_id}`)}
                              variant="outline"
                              className="border-slate-700 bg-slate-900 text-slate-100 hover:bg-slate-800"
                            >
                              <ExternalLink className="mr-2 h-4 w-4" />
                              Open Event
                            </Button>

                            <Button
                              size="sm"
                              onClick={() => handleViewReport(event.event_id)}
                              className="bg-cyan-600 text-white hover:bg-cyan-500"
                            >
                              <Eye className="mr-2 h-4 w-4" />
                              View Report
                            </Button>

                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleSingleDownload(event, "pdf")}
                              disabled={busyKey === `pdf-${event.event_id}`}
                              className="border-slate-700 bg-slate-900 text-slate-100 hover:bg-slate-800"
                            >
                              {busyKey === `pdf-${event.event_id}` ? (
                                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                              ) : (
                                <FileText className="mr-2 h-4 w-4" />
                              )}
                              PDF
                            </Button>

                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleSingleDownload(event, "excel")}
                              disabled={busyKey === `excel-${event.event_id}`}
                              className="border-slate-700 bg-slate-900 text-slate-100 hover:bg-slate-800"
                            >
                              {busyKey === `excel-${event.event_id}` ? (
                                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                              ) : (
                                <FileSpreadsheet className="mr-2 h-4 w-4" />
                              )}
                              Excel
                            </Button>

                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleOpenDebugJson(event.event_id)}
                              className="border-slate-700 bg-slate-900 text-slate-100 hover:bg-slate-800"
                            >
                              <Code2 className="mr-2 h-4 w-4" />
                              Debug JSON
                            </Button>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>

        {selectedEvents.length > 0 && (
          <Card className="border-slate-800 bg-slate-950/70 text-slate-100">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-white">
                <CheckCircle2 className="h-5 w-5 text-emerald-400" />
                Current Selection
              </CardTitle>
            </CardHeader>

            <CardContent>
              <div className="flex flex-wrap gap-2">
                {selectedEvents.map((event) => (
                  <Badge
                    key={event.event_id}
                    className="border-cyan-500/30 bg-cyan-500/10 text-cyan-300 hover:bg-cyan-500/10"
                  >
                    #{event.event_id} • {event.event_name}
                  </Badge>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        <Card className="border-slate-800 bg-slate-950/70 text-slate-100">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-white">
              <Download className="h-5 w-5 text-fuchsia-400" />
              Reporting Workflow
            </CardTitle>
          </CardHeader>

          <CardContent>
            <div className="grid gap-4 md:grid-cols-3">
              <div className="rounded-xl border border-slate-800 bg-slate-900/70 p-4">
                <div className="mb-2 flex items-center gap-2 text-white">
                  <CheckCircle2 className="h-4 w-4 text-emerald-400" />
                  What works now
                </div>
                <p className="text-sm text-slate-400">
                  You can filter events by date range, select one or many events, inspect summary metrics,
                  open a live report viewer, and export each event’s PDF and Excel report immediately.
                </p>
              </div>

              <div className="rounded-xl border border-slate-800 bg-slate-900/70 p-4">
                <div className="mb-2 flex items-center gap-2 text-white">
                  <AlertTriangle className="h-4 w-4 text-amber-400" />
                  Current backend limit
                </div>
                <p className="text-sm text-slate-400">
                  Your backend still exports reports per event. Period selection already supports grouped review,
                  but one true combined period PDF/Excel route would be the next backend improvement.
                </p>
              </div>

              <div className="rounded-xl border border-slate-800 bg-slate-900/70 p-4">
                <div className="mb-2 flex items-center gap-2 text-white">
                  <Sparkles className="h-4 w-4 text-cyan-400" />
                  Best next step
                </div>
                <p className="text-sm text-slate-400">
                  After this viewer is live, add one backend route like
                  <span className="mx-1 font-mono text-cyan-300">/event/reports/period</span>
                  to generate a combined management pack for the selected date range.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}